import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {TFunction} from 'i18next';
import type {ProtocolRegion} from '../regions';
import type {RegionStatus} from './RegionSidebar';

interface ReviewStepProps {
  regions: ProtocolRegion[];
  getRegionStatus: (regionId: string) => RegionStatus;
  getRegionCapturedCount: (regionId: string) => number;
  onEditRegion: (stepIndex: number) => void;
  t: TFunction;
}

export default function ReviewStep({
  regions,
  getRegionStatus,
  getRegionCapturedCount,
  onEditRegion,
  t
}: ReviewStepProps) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" component="h2" gutterBottom>
          {t('shared/imageProtocol:review')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('shared/imageProtocol:reviewDescription')}
        </Typography>
      </Box>
      <Paper variant="outlined">
        <List>
          {regions.map((region, index) => {
            const status = getRegionStatus(region.id);
            const count = getRegionCapturedCount(region.id);
            return (
              <ListItem
                key={region.id}
                divider={index < regions.length - 1}
                secondaryAction={
                  <Typography
                    component="button"
                    variant="body2"
                    onClick={() => onEditRegion(index)}
                    sx={{
                      color: 'primary.main',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      textDecoration: 'underline'
                    }}
                  >
                    {t('shared/common:edit')}
                  </Typography>
                }
                sx={{pr: 12}}
              >
                <ListItemIcon>
                  <ReviewStatusIcon status={status} />
                </ListItemIcon>
                <ListItemText
                  primary={`${region.number}. ${t('shared/imageProtocol:hs.regions.' + region.id + '.name')}`}
                  secondary={getSecondaryLabel({status, count, t})}
                  primaryTypographyProps={{
                    sx: {
                      textDecoration: status === 'skipped' ? 'line-through' : 'none'
                    }
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Paper>
    </Stack>
  );
}

function getSecondaryLabel({
  status,
  count,
  t
}: {
  status: RegionStatus;
  count: number;
  t: TFunction;
}) {
  if (status === 'skipped') {
    return t('shared/imageProtocol:reviewSkipped');
  }
  if (count === 0) {
    return t('shared/imageProtocol:reviewPending');
  }
  return t('shared/imageProtocol:reviewCaptured', {count});
}

function ReviewStatusIcon({status}: {status: RegionStatus}) {
  if (status === 'complete') {
    return <CheckCircleIcon color="success" />;
  }
  if (status === 'skipped') {
    return <SkipNextIcon sx={{color: 'text.disabled'}} />;
  }
  return <RadioButtonUncheckedIcon sx={{color: 'text.disabled'}} />;
}
